import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  StatusBadge,
  Button,
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import {
  useOutstanding,
  useSettlements,
  useRecordSettlement,
  useSettlementConfirm,
  useOutstandingDispute,
} from '@/hooks/useKosh';
import { outstandingsApi } from '@/services/kosh-api';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { outstandingStatusVariants, settlementStatusVariants } from '@/config/kosh-status';
import { formatMoney, shortDate } from '@/lib/format';

export function OutstandingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const q = useOutstanding(id);
  const settlementsQ = useSettlements();
  const record = useRecordSettlement();
  const confirm = useSettlementConfirm();
  const dispute = useOutstandingDispute();

  const writeOff = useMutation({
    mutationFn: () => outstandingsApi.writeOff(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kosh', 'outstandings'] }),
  });

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');

  const settlements = useMemo(
    () => (settlementsQ.data ?? []).filter((s) => s.outstandingId === id),
    [settlementsQ.data, id],
  );

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(o) => {
          const ccy = o.invoice?.currency ?? 'INR';
          const settleable = o.status === 'OPEN' || o.status === 'PARTIALLY_SETTLED';
          const submit = () => {
            const major = Number(amount);
            if (!id || !major || major <= 0) return;
            record.mutate(
              { outstandingId: id, amount: Math.round(major * 100), currency: ccy, method, reference: reference || undefined },
              { onSuccess: () => { setAmount(''); setReference(''); } },
            );
          };
          return (
            <>
              <PageHeader
                eyebrow={
                  <Link to="/receivables" className="inline-flex items-center gap-1 hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5" /> Receivables
                  </Link>
                }
                title={o.invoice?.invoiceNumber ?? 'Outstanding'}
                meta={<StatusBadge status={o.status} variants={outstandingStatusVariants} />}
                actions={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={dispute.isPending}
                      onClick={() => id && dispute.mutate({ id, inDispute: !o.inDispute })}
                    >
                      <AlertTriangle className="h-4 w-4" />
                      {o.inDispute ? 'Clear dispute' : 'Mark disputed'}
                    </Button>
                    {settleable && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={writeOff.isPending}
                        onClick={() => writeOff.mutate()}
                      >
                        Write off
                      </Button>
                    )}
                  </div>
                }
              />

              <DashboardGrid columns={2}>
                <DashboardCard title="Balance">
                  <div className="space-y-1.5 text-[13px]">
                    <Row k="Original" v={formatMoney(o.originalAmount, ccy)} />
                    <Row k="Credit adjustments" v={formatMoney(o.creditAdjustments, ccy)} />
                    <Row k="Debit adjustments" v={formatMoney(o.debitAdjustments, ccy)} />
                    <Row k="Settled" v={formatMoney(o.settledAmount, ccy)} />
                    <div className="flex justify-between border-t border-border pt-1.5 text-[15px] font-bold">
                      <span>Balance</span>
                      <span className="tabular-nums">{formatMoney(o.balanceAmount, ccy)}</span>
                    </div>
                    <Row k="Due" v={shortDate(o.dueDate)} />
                  </div>
                </DashboardCard>

                <DashboardCard title="Record settlement" description={settleable ? undefined : 'Fully settled.'}>
                  {settleable ? (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="amt">Amount ({ccy})</Label>
                        <Input
                          id="amt"
                          type="number"
                          min="0"
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="method">Method</Label>
                        <select
                          id="method"
                          value={method}
                          onChange={(e) => setMethod(e.target.value)}
                          className="h-9 w-full rounded-md border border-input bg-background px-2 text-[13px]"
                        >
                          <option value="BANK_TRANSFER">Bank transfer</option>
                          <option value="UPI">UPI</option>
                          <option value="CARD">Card</option>
                          <option value="CASH">Cash</option>
                          <option value="CHEQUE">Cheque</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="ref">Reference</Label>
                        <Input
                          id="ref"
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          placeholder="UTR / txn id (optional)"
                        />
                      </div>
                      <Button size="sm" disabled={record.isPending || !amount} onClick={submit}>
                        Record settlement
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">This receivable is closed.</p>
                  )}
                </DashboardCard>
              </DashboardGrid>

              <DashboardCard title="Settlements" flushBody>
                {settlements.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No settlements recorded yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {settlements.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="text-muted-foreground">{shortDate(s.settledAt ?? s.createdAt)}</TableCell>
                          <TableCell>{s.method ?? '—'}</TableCell>
                          <TableCell className="font-mono text-[12px]">{s.reference ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(s.amount, s.currency)}</TableCell>
                          <TableCell>
                            <StatusBadge status={s.status} variants={settlementStatusVariants} />
                          </TableCell>
                          <TableCell className="text-right">
                            {s.status === 'RECORDED' && (
                              <button
                                onClick={() => confirm.mutate(s.id)}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] text-[hsl(142_70%_35%)] hover:bg-muted"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DashboardCard>
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="tabular-nums">{v}</span>
    </div>
  );
}
